/**
 * **添加节点** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 「节点从哪来、落在哪、加完选谁」：
 *
 *   `addSections`    添加菜单的分组（分类当标题、节点类型当项）
 *   `addNodeAt`      真建一个节点：算落点、进图、选中并设活动
 *   `openAddMenu`    开「添加节点」菜单（落点三档优先）
 *   `focusNode`      把镜头移过去 + 选中 + 设为活动节点
 *   `toFlowPoint`    屏幕坐标 → 画布坐标
 *
 * ★ `addSections` 是**单一真相**，同时喂两个地方：
 *   右键菜单里的「添加节点」二级菜单，和 `Shift+A` 的一级菜单 ——
 *   不会出现「快捷方式里有的节点，右键里找不到」。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `menu` 用 getter/setter 注入（而不是注入整个 ref）—— 这样宿主把菜单的
 * 数据结构 `LinkMenuContext` 留在原处，composable 不必反向 import。
 */
import { computed, nextTick } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import { republish } from '@/graph/documentSync'
import type { FlowNode as Node } from '@/graph/flowTypes'
import { selectOnly, viewportOf } from '@/graph/flowStore'
import type { FlowStoreApi } from '@/graph/flowStore'
import { ASSETS } from '@/playground/nodeTypes'
import { describeAsset, makeAssetModel, type GroupAssetDef } from '@/node/asset'
import {
  NODE_CATEGORIES,
  NODE_TYPES,
  categoryLabel,
  makeModelOfType,
  nodeTypeOf,
} from '@/playground/nodeTypes'
import type { MenuItem, MenuSection } from '@/types/menu'
import type { GraphNodeModel } from '@/types/node'

export interface AddNodeCtx {
  nodes: { value: Node[] }
  flowStore: { value: FlowStoreApi | null }
  graphRef: { value: HTMLElement | null }
  /** 活动节点（加完要设成新节点，属性面板才跟得过来） */
  activeNodeId: { value: string | null }
  /**
   * 最近用过的节点类型（已经在目录里的那些）—— 菜单顶部那一组。
   *
   * 注入而不是在这里自己读 localStorage：那样测试要清盘、
   * 而且这个 composable 会多出一个「持久化」的职责。
   */
  recentTypes: { value: string[] }
  /** 记一次「用过这个类型」（菜单顶部的「最近」靠它） */
  rememberNodeType: (type: string) => void
  /** 当前菜单的落点（没有菜单时为 null）—— 只用它的 x / y */
  menuPoint: () => { x: number; y: number } | null
  /** 打开「添加节点」菜单（菜单模型在宿主里） */
  /** 开菜单 —— 带上 kind（「添加节点」是 `add`，「添加资产」是 `asset`） */
  openMenuAt: (point: { x: number; y: number }, kind: 'add' | 'asset') => void
  /** 鼠标在画布上的最后位置（宿主在 pointermove 里写，`Shift+A` 要读） */
  lastCanvasPoint: { value: { x: number; y: number } | null }
  nextNodeId: (type: string) => string
  makeFlowNode: (model: GraphNodeModel, position: { x: number; y: number }) => Node
  commit: (label: string, coalesceMs?: number) => void
  showNotice: (message: string) => void
}

export function useAddNode(ctx: AddNodeCtx) {
  const {
    recentTypes,
    rememberNodeType,
    nodes,
    flowStore,
    graphRef,
    activeNodeId,
    menuPoint,
    openMenuAt,
    lastCanvasPoint,
    nextNodeId,
    makeFlowNode,
    commit,
    showNotice,
  } = ctx

/*
 * ⚠️ 这里原来有一份 `CATEGORY_TITLES`（data→数据 …）——**已经删掉**。
 * 分类名是**包的文案**，单一来源在 `domains/print/index.ts` 的
 * `PRINT_PACKAGE.categories` 里，通过注册表的 `categoryLabel()` 查。
 * 内核里再抄一份的表现是「加了新分类，菜单里显示成英文标识」。
 */

/**
 * 添加菜单的分组：**分类是分组标题，节点类型是项**。
 *
 * ★ 这一份同时用在两个地方，所以是单一真相：
 *   · 右键菜单里作为「添加节点」的**二级菜单**
 *   · `Shift+A` 直接把它当**一级菜单**弹出来
 * 两处内容永远一致 —— 不会出现「快捷方式里有的节点，右键里找不到」。
 *
 * 落点读的是当前菜单的位置：右键进来就是右键的地方，
 * Shift+A 进来就是视口中心（见 openAddMenu）。
 */
/** 造一个「添加这个节点」的菜单项（最近那一组和分类组共用） */
function addItemOf(def: (typeof NODE_TYPES)[number]): MenuItem {
  return {
    id: `add:${def.type}`,
    label: def.title,
    keywords: def.keywords,
    /* ⚠️ hint 用**类型标识**：菜单里显示「逻辑 · 条件选择」时，
     * 想知道它到底是哪个类型（写文档、跟人沟通）只能靠它 */
    hint: def.type,
    run: () => {
      const at = menuPoint()
      addNodeAt(def.type, at ? { x: at.x, y: at.y } : undefined)
    },
  }
}

function buildAddSections(): MenuSection[] {
  const groups = new Map<string, MenuItem[]>()
  for (const def of NODE_TYPES) {
    // 接通节点是「对线操作」的产物，不进添加菜单
    if (def.hidden) continue
    const list = groups.get(def.category) ?? []
    list.push(addItemOf(def))
    groups.set(def.category, list)
  }

  /*
   * ⚠️ 分组的**顺序**要按注册表的分类顺序来，**不能**按「节点登记的先后」。
   *
   * 原来这里是 `[...groups]` —— Map 的插入顺序 = 节点在目录里的先后，
   * 于是 `categoryOrder`（数据 → 常量 → 工具 → 函数）**完全没生效**，
   * 实际渲染出来是「常量 · 工具 · 函数 · 数据」，数据源被挤到最后。
   * 而当时的断言只查了 `NODE_CATEGORIES` 这个数组的顺序 ——
   * **假绿**：它测的是注册表，不是菜单。
   * （真机上没发现是因为这一轮才真正渲染出来看，见 `verify:render`。）
   */
  const sections: MenuSection[] = NODE_CATEGORIES.flatMap((category) => {
    const items = groups.get(category.id)
    if (!items || items.length === 0) return []
    return [{ id: `cat-${category.id}`, title: categoryLabel(category.id), items }]
  })

  /*
   * ★ 「最近」放在**最前面**。
   *
   * 搭模板时反复加的就是那几个节点；把最近用过的提到一级，
   * 比任何层级优化都有效（Blender 的 Quick Favorites 是同一个思路，
   * 但那个要手动收藏 —— 我们自动记，见 `useRecentNodes`）。
   *
   * ⚠️ 只列**还在目录里**的类型：`recentTypes` 已经过滤过包卸载的情况，
   * 但这里仍要查一次 `nodeTypeOf` —— 拿不到定义就造不出菜单项。
   */
  const recent: MenuItem[] = []
  for (const type of recentTypes.value) {
    const def = nodeTypeOf(type)
    if (!def || def.hidden) continue
    recent.push(addItemOf(def))
  }
  if (recent.length > 0) {
    sections.unshift({
      id: 'cat-recent',
      title: '最近',
      titleStyle: 'divider',
      items: recent,
    })
  }

  return sections
}

/** 分组内容是静态的，但落点要现算 —— 所以 run 里才读 menu.value */
const addSections = computed<MenuSection[]>(buildAddSections)

/**
 * 「添加资产」菜单的分组 —— 资产名就是项名。
 *
 * ⚠️ 不按 `category` 分组：资产通常只有几个，再套一层分类
 * 会让「点两下才能放一个」——而它的价值正是「一下就有」。
 * 分组只用来区分**来自哪个包**（包多了才会需要）。
 */
const assetSections = computed<MenuSection[]>(() => {
  if (ASSETS.length === 0) return []
  return [
    {
      id: 'assets',
      items: ASSETS.map((asset) => ({
        id: `asset:${asset.id}`,
        label: asset.label,
        hint: describeAsset(asset),
        keywords: asset.keywords,
        run: () => {
          const at = menuPoint()
          addAssetAt(asset, at ? { x: at.x, y: at.y } : undefined)
        },
      })),
    },
  ]
})

/**
 * 放一个**资产**（组节点资产）。
 *
 * ⚠️ 放下去的是 `type: 'group'` 的普通组节点，只是 `rows` / `graph`
 * 来自资产 —— 求值、进组编辑、子图层级全都照旧走组那一套，
 * 一行都没为资产改。资产的身份体现在 `title` 上。
 */
function addAssetAt(asset: GroupAssetDef, screenPoint?: { x: number; y: number }): string | null {
  const id = nextNodeId('group')
  const model = makeAssetModel(asset, id)
  commit(`添加资产「${asset.label}」`)
  const position = toFlowPoint(screenPoint ?? viewportCenter())

  nodes.value = [
    ...(nodes.value as unknown as Record<string, unknown>[]),
    makeFlowNode(model, position) as unknown as Record<string, unknown>,
  ] as unknown as Node[]

  nextTick(() => flowStore.value?.updateNodeInternals())
  /* 和 `addNodeAt` 一致：新建完选中并设为活动（属性面板才会跟过来） */
  focusNode(id)
  showNotice(`已添加资产：${asset.label}`)
  return id
}

/** 屏幕坐标 → 画布坐标（图引擎没就绪时按 1:1 兜底） */
function toFlowPoint(point: { x: number; y: number }): { x: number; y: number } {
  const store = flowStore.value
  if (store?.screenToFlowCoordinate) return store.screenToFlowCoordinate(point)
  const viewport = viewportOf(store)
  return {
    x: (point.x - viewport.x) / viewport.zoom,
    y: (point.y - viewport.y) / viewport.zoom,
  }
}

/** 画布可视区中心（屏幕坐标）—— Shift+A 没有鼠标位置可用 */
function viewportCenter(): { x: number; y: number } {
  const host = graphRef.value?.getBoundingClientRect()
  if (host) return { x: host.left + host.width / 2, y: host.top + host.height / 2 }
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 }
}

/**
 * 把镜头移到某个节点上，并**只选中它**。
 *
 * ⚠️ 两件容易漏的事都在这儿：
 *   · 只调 `addSelectedNodes` 是不够的 —— 属性面板跟的是**活动节点**
 *     （`activeNodeId`），而它只在用户点击节点时才会被设置。
 *     程序化选中之后面板不会切过来（表现：「节点建出来了，但改不了名称」）；
 *   · 只「选中」也是不够的 —— 节点在屏幕外时用户什么都看不见，
 *     （表现：「点了定位没反应」）所以还要 fitView 过去。
 */
function focusNode(id: string) {
  activeNodeId.value = id
  nextTick(() => {
    /*
     * ⚠️ **替换**选中，不是追加（`selectOnly`）。这里原来调的是
     * `addSelectedNodes`（追加），于是「选中 A → 加一个新节点」之后
     * **A 还亮着**：多选态下再拖一下会把 A 一起拖走，而用户以为
     * 自己只是加了个节点。加节点 / 加资产 / 从连线菜单新建都走这里。
     */
    /* ⚠️ 就地改的标志必须 republish 才会被别的窗口 / 面板看到（见 `selectOnly`） */
    const dirty = selectOnly(nodes.value as unknown as { id: string; selected?: boolean }[], [id])
    nodes.value = republish(nodes.value, dirty)
    flowStore.value?.fitView?.({
      nodes: [{ id }],
      padding: 0.5,
      duration: 240,
      maxZoom: 1.1,
    })
  })
}

/** 新建一个节点，返回它的 id */
function addNodeAt(type: string, screenPoint?: { x: number; y: number }): string | null {
  /* 记「最近用过」——加成功之前先记，失败了也无所谓（它只是个快捷方式） */
  rememberNodeType(type)
  const model0 = nodeTypeOf(type)
  if (!model0) {
    showNotice(`没有登记的节点类型：${type}`)
    return null
  }
  const id = nextNodeId(type)
  const model = makeModelOfType(type, id)
  if (!model) return null

  commit('添加节点')
  const position = toFlowPoint(screenPoint ?? viewportCenter())

  nodes.value = [
    ...(nodes.value as unknown as Record<string, unknown>[]),
    makeFlowNode(model, position) as unknown as Record<string, unknown>,
  ] as unknown as Node[]

  nextTick(() => flowStore.value?.updateNodeInternals())
  // 新建完选中并**设为活动** —— 否则属性面板不会出现，改不了名称
  focusNode(id)
  showNotice(`已添加：${model.title}`)
  return id
}

/**
 * 打开「添加节点」菜单。
 *
 * 落点优先级：显式传入（双击画布）> 鼠标最后位置（Shift+A）> 视口中心（兜底）。
 * 视口中心只在「鼠标还没进过画布」时才会用到。
 */
/**
 * 打开「添加资产」菜单。
 *
 * 落点规则和 `openAddMenu` **完全一致**（显式 > 鼠标最后位置 > 视口中心）——
 * 不另写一套，否则两个入口的落点行为会慢慢分叉。
 */
function openAssetMenu(point?: { x: number; y: number }) {
  const at = point ?? lastCanvasPoint.value ?? viewportCenter()
  openMenuAt(at, 'asset')
}

function openAddMenu(point?: { x: number; y: number }) {
  const at = point ?? lastCanvasPoint.value ?? viewportCenter()
  openMenuAt(at, 'add')
}

  return {
    addSections,
    assetSections,
    toFlowPoint,
    viewportCenter,
    focusNode,
    addNodeAt,
    addAssetAt,
    openAddMenu,
    openAssetMenu,
  }
}
